import { useMediaQuery as useMQ } from 'react-responsive';
import facepaint from 'facepaint';
import * as R from 'ramda';
import theme from '@snaphunt/snaphunt-theme';

// * https://css-tricks.com/snippets/css/media-queries-for-standard-devices/

/*
**************************************************
  MOBILE
**************************************************
*/
const useMediaQuery = () => {
  /**
   * iPhones
   */
  const isIPhone4 = useMQ({
    query:
      'only screen and (min-device-width: 320px) and (max-device-width: 480px) and (-webkit-min-device-pixel-ratio: 2)',
  });

  const isIphone5 = useMQ({
    query:
      'only screen and (min-device-width: 320px) and (max-device-width: 568px) and (-webkit-min-device-pixel-ratio: 2)',
  });

  const isIphone678 = useMQ({
    query:
      'only screen and (min-device-width: 375px) and (max-device-width: 667px) and (-webkit-min-device-pixel-ratio: 2)',
  });

  const isIphone678Plus = useMQ({
    query:
      'only screen and (min-device-width: 414px) and (max-device-width: 736px) and (-webkit-min-device-pixel-ratio: 3)',
  });

  const isIphoneX = useMQ({
    query:
      'only screen and (min-device-width: 375px) and (max-device-width: 812px) and (-webkit-min-device-pixel-ratio: 3)',
  });

  const isIPhone =
    isIPhone4 || isIphone5 || isIphone678 || isIphone678Plus || isIphoneX;

  /**
   * iPads
   */
  const isIpadAir12 = useMQ({
    query:
      'only screen and (min-device-width: 768px) and (max-device-width: 1024px) and (-webkit-min-device-pixel-ratio: 1)',
  });

  const isIpad34 = useMQ({
    query:
      'only screen and (min-device-width: 768px) and (max-device-width: 1024px) and (-webkit-min-device-pixel-ratio: 2)',
  });

  const isIpadPro10 = useMQ({
    query:
      'only screen and (min-device-width: 834px) and (max-device-width: 1112px) and (-webkit-min-device-pixel-ratio: 2)',
  });

  const isIPad = isIpadAir12 || isIpad34 || isIpadPro10;

  return { isMobile: isIPhone, isTablet: isIPad };
};

const { breakpoints } = theme;

/**
 * value returned will be same as material ui's hidden api
 * https://material-ui.com/api/hidden/
 */
const useBreakpoints = () => {
  const isXsDown = useMQ({ maxDeviceWidth: breakpoints.sm - 1 });

  const isSmDown = useMQ({ maxDeviceWidth: breakpoints.md - 1 });

  const isMdDown = useMQ({ maxDeviceWidth: breakpoints.lg - 1 });

  const isLgDown = useMQ({ maxDeviceWidth: breakpoints.xl - 1 });

  return {
    isXsDown,
    isSmDown,
    isMdDown,
    isLgDown,
  };
};

const addObjectBreakpointsAPI = facepainter => {
  // Find all the css props whose value contains breakpoints, i.e. { default, xs, sm, md, lg, xl }
  // Convert the breakpoints object to array of 6 values, corresponding to the breakpoints
  // in the correct order; if breakpoint name is missing, use 'null' value.
  // Discard unknown breakpoint names.
  // For example:
  // { padding: { default: 0, xs: 20, sm: 50, lg: 70 } } => { padding: [0, 20, 50, null, 70, null] }
  // { '&:hover': { color: { xs: 'green', md: 'red' } } } => { '&:hover': { color: [null, 'green', null, 'red', null, null] } }
  const breakpointNames = ['default', ...R.keys(breakpoints)];

  /**
   * Convert a breakpoints object to a breakpoints array
   * @param {Object} breakpointsObj - i.e. { xs: 20, sm: 50, lg: 70 }
   * @returns {Array} breakpoints array, i.e. [null, 20, 50, null, 70, null]
   */
  const convertToArrayBreakpointsAPI = breakpointsObj =>
    // eslint-disable-next-line
    R.map(R.propOr(null, R.__, breakpointsObj))(breakpointNames);

  const isBreakpointsObj = R.pipe(
    R.keys,
    R.intersection(breakpointNames),
    R.complement(R.isEmpty),
  );

  // TODO Move to kickass-utilities
  // eslint-disable-next-line no-proto
  const isObjectLiteral = v => v.__proto__ === Object.prototype;

  // ? Not using point free style due to recursion
  const convertRecursively = obj =>
    R.map(
      R.when(
        isObjectLiteral,
        R.ifElse(
          isBreakpointsObj,
          convertToArrayBreakpointsAPI,
          convertRecursively,
        ),
      ),
    )(obj);

  return R.pipe(convertRecursively, facepainter);
};

const useMediaQueryStyles = addObjectBreakpointsAPI(
  facepaint(
    R.pipe(
      R.values,
      R.map(bp => `@media (min-width: ${bp}px)`),
    )(breakpoints),
  ),
);

export { useMediaQueryStyles, useBreakpoints };

export default useMediaQuery;
